// Die Tastenkuerzel des Rahmens (ST-UX-001, CM-UX-001) als reine Funktion plus
// ein Haken am Dokument.
//
// WCAG 2.1.4 (Character Key Shortcuts): Einzeltasten muessen abschaltbar sein
// und wirken nie, solange der Fokus in einem Eingabefeld steht. Strg/Cmd+K ist
// ein Akkord und bleibt auch ausgeschaltet.

import { useEffect, useRef, useState } from 'react';

export type KuerzelBefehl = 'palette' | 'palette-fokus' | 'anlegen' | 'notiz' | 'kontakte' | 'wiedervorlagen' | 'uebersicht';

export type KuerzelErgebnis = { readonly art: 'befehl'; readonly befehl: KuerzelBefehl } | { readonly art: 'g-gemerkt' };

export interface TastenEreignis {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  /** Der Fokus steht in input, textarea, select oder [contenteditable]. */
  readonly imFeld: boolean;
}

export interface KuerzelZustand {
  readonly einzeltasten: boolean;
  readonly paletteOffen: boolean;
  /** Ein anderer Dialog oder ein Menue ist offen. */
  readonly anderesOffen: boolean;
  readonly darfAnlegen: boolean;
  readonly notizAngemeldet: boolean;
  /** Zeitpunkt des letzten G, sonst undefined. */
  readonly gSeit: number | undefined;
  readonly jetzt: number;
}

/** So lange gilt ein G als gemerkt. */
export const G_FRIST_MS = 1200;

export function kuerzelFuer(ereignis: TastenEreignis, zustand: KuerzelZustand): KuerzelErgebnis | undefined {
  const taste = ereignis.key.toLowerCase();
  if ((ereignis.ctrlKey || ereignis.metaKey) && !ereignis.altKey && taste === 'k') {
    if (zustand.paletteOffen) {
      return { art: 'befehl', befehl: 'palette-fokus' };
    }
    return zustand.anderesOffen ? undefined : { art: 'befehl', befehl: 'palette' };
  }
  if (!zustand.einzeltasten || ereignis.ctrlKey || ereignis.metaKey || ereignis.altKey || ereignis.imFeld) {
    return undefined;
  }
  if (zustand.paletteOffen || zustand.anderesOffen) {
    return undefined;
  }
  const gGilt = zustand.gSeit !== undefined && zustand.jetzt - zustand.gSeit <= G_FRIST_MS;
  if (gGilt && taste === 'k') {
    return { art: 'befehl', befehl: 'kontakte' };
  }
  if (gGilt && taste === 'w') {
    return { art: 'befehl', befehl: 'wiedervorlagen' };
  }
  switch (taste) {
    case 'g':
      return { art: 'g-gemerkt' };
    case 'n':
      return zustand.darfAnlegen ? { art: 'befehl', befehl: 'anlegen' } : undefined;
    case 'c':
      return zustand.notizAngemeldet ? { art: 'befehl', befehl: 'notiz' } : undefined;
    case '?':
      return { art: 'befehl', befehl: 'uebersicht' };
    case '/':
      return { art: 'befehl', befehl: 'palette' };
    default:
      return undefined;
  }
}

const SCHLUESSEL = 'liaison.einzeltasten';

/** Ohne Speicher oder bei einem Fehler gilt "an". */
export function einzeltastenGespeichert(): boolean {
  try {
    return window.localStorage.getItem(SCHLUESSEL) !== 'aus';
  } catch {
    return true;
  }
}

function einzeltastenSpeichern(an: boolean): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, an ? 'an' : 'aus');
  } catch {
    // Komfortangabe: ohne Speicher gilt sie bis zum Neuladen.
  }
}

export function useEinzeltasten(): readonly [boolean, (an: boolean) => void] {
  const [an, setAn] = useState(einzeltastenGespeichert);
  return [
    an,
    (neu) => {
      einzeltastenSpeichern(neu);
      setAn(neu);
    },
  ] as const;
}

/**
 * Felder, in denen Buchstaben Eingabe sind. Neben den Formularelementen zaehlen
 * Fluents Auswahlfelder: ein Dropdown ist ein Knopf mit role=combobox, und die
 * Tastatursuche seiner Liste tippt Buchstaben ("Journ" + Enter).
 */
const EINGABEFELD =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="combobox"], [role="textbox"], [role="searchbox"], [role="spinbutton"], [role="listbox"], [role="option"]';

function imEingabefeld(ziel: EventTarget | null): boolean {
  return ziel instanceof HTMLElement && (ziel.isContentEditable || ziel.closest(EINGABEFELD) !== null);
}

/** Dialoge und Menues ausser der Palette selbst. */
function anderesOffen(): boolean {
  return document.querySelector('[role="dialog"]:not([data-befehlspalette]), [role="alertdialog"], [role="menu"]') !== null;
}

export function useTastenkuerzel(
  optionen: {
    /** Ohne Angebot (nicht angemeldet) bleibt jede Taste dem Browser. */
    readonly verfuegbar: boolean;
    readonly einzeltasten: boolean;
    readonly paletteOffen: boolean;
    readonly darfAnlegen: boolean;
    readonly notizAngemeldet: boolean;
  },
  ausfuehren: (befehl: KuerzelBefehl) => void,
): void {
  const stand = useRef({ optionen, ausfuehren });
  stand.current = { optionen, ausfuehren };
  const gSeit = useRef<number | undefined>(undefined);

  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (!stand.current.optionen.verfuegbar || ereignis.defaultPrevented || ereignis.isComposing) {
        return;
      }
      const jetzt = Date.now();
      const ergebnis = kuerzelFuer(
        { key: ereignis.key, ctrlKey: ereignis.ctrlKey, metaKey: ereignis.metaKey, altKey: ereignis.altKey, imFeld: imEingabefeld(ereignis.target) },
        { ...stand.current.optionen, anderesOffen: anderesOffen(), gSeit: gSeit.current, jetzt },
      );
      gSeit.current = ergebnis?.art === 'g-gemerkt' ? jetzt : undefined;
      if (ergebnis === undefined) {
        return;
      }
      ereignis.preventDefault();
      if (ergebnis.art === 'befehl') {
        stand.current.ausfuehren(ergebnis.befehl);
      }
    };
    document.addEventListener('keydown', beiTaste);
    return () => document.removeEventListener('keydown', beiTaste);
  }, []);
}

/** Mac-Tastatur: dort heisst der Akkord Cmd+K und steht als "⌘ K" im Kopf. */
export function istMacTastatur(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const plattform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? '';
  return /mac|iphone|ipad/i.test(plattform);
}

export const PALETTE_ID_FELD = 'befehlspalette-feld';

/** Die Zeilen der Uebersicht: nur Kuerzel, die es gibt (ADR-0002). */
export interface KuerzelZeile {
  readonly tasten: readonly string[];
  readonly wirkung: string;
  /** "dann": die Tasten nacheinander, nicht zugleich. */
  readonly nacheinander?: boolean;
}

export function kuerzelZeilen(
  angebot: { readonly darfAnlegen: boolean; readonly darfKontakte: boolean; readonly darfWiedervorlagen: boolean; readonly notizAngemeldet: boolean },
  mac: boolean,
): readonly KuerzelZeile[] {
  return [
    { tasten: [mac ? '⌘' : 'Strg', 'K'], wirkung: 'Kontakt oder Befehl finden (Schnellzugriff öffnen)' },
    { tasten: ['/'], wirkung: 'Schnellzugriff öffnen' },
    ...(angebot.darfAnlegen ? [{ tasten: ['N'], wirkung: 'Kontakt anlegen' }] : []),
    ...(angebot.notizAngemeldet ? [{ tasten: ['C'], wirkung: 'Notiz zum Kontakt schreiben – auf der Kontaktseite' }] : []),
    ...(angebot.darfKontakte ? [{ tasten: ['G', 'K'], wirkung: 'Zu den Kontakten', nacheinander: true }] : []),
    ...(angebot.darfWiedervorlagen ? [{ tasten: ['G', 'W'], wirkung: 'Zu den Wiedervorlagen', nacheinander: true }] : []),
    { tasten: ['?'], wirkung: 'Diese Übersicht' },
  ];
}
